import { createReducer } from 'src/helpers/reducer';
import {
  ILoadChecklistsSuccessPayload,
  ILoadChecklistDetailSuccessPayload,
  ILoadShiftHistoryPositionsSuccessPayload,
  ILoadDataSuccessPayload,
  ILoadLTenureTracksSuccessPayload,
  ILoadShiftsSuccessPayload,
  IViewAgencyWorkerState,
} from '../types';
import actionTypes from './actionTypes';

const initState: IViewAgencyWorkerState = {
  isLoading: false,
  workerIdentity: undefined,
  isShiftsLoading: false,
  workerShifts: undefined,
  tenureTracksByAgency: {},
  isTenureTracksLoading: false,
  shiftHistoryPositionsByAgency: {},
  isShiftHistoryPositionsLoading: false,
  checklistsByAgency: {},
  isChecklistsLoading: false,
  isChecklistDetailLoading: false,
  checklistDetail: undefined,
};

function onLoad(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isLoading: true,
  };
}

function onLoadSuccess(
  state: IViewAgencyWorkerState,
  { payload }: { payload: ILoadDataSuccessPayload },
): IViewAgencyWorkerState {
  const { worker_identity } = payload;

  return {
    ...state,
    workerIdentity: worker_identity,
    isLoading: false,
  };
}

function onLoadError(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isLoading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function onLoadShifts(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isShiftsLoading: true,
  };
}

function onLoadShiftsSuccess(
  state: IViewAgencyWorkerState,
  { payload }: { payload: ILoadShiftsSuccessPayload },
): IViewAgencyWorkerState {
  const { records, totalCount, filteredCount, filters } = payload;

  return {
    ...state,
    workerShifts: {
      records,
      totalCount,
      filteredCount,
      filters,
    },
    isShiftsLoading: false,
  };
}

function onLoadShiftsError(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isShiftsLoading: false,
  };
}

function onLoadTenureTracks(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isTenureTracksLoading: true,
  };
}

function onLoadTenureTracksSuccess(
  state: IViewAgencyWorkerState,
  { payload }: { payload: ILoadLTenureTracksSuccessPayload },
): IViewAgencyWorkerState {
  const { agency_id, records, totalCount, filteredCount, filters } = payload;

  return {
    ...state,
    tenureTracksByAgency: {
      ...state.tenureTracksByAgency,
      [agency_id]: {
        records,
        totalCount,
        filteredCount,
        filters,
      },
    },
    isTenureTracksLoading: false,
  };
}

function onLoadTenureTracksError(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isTenureTracksLoading: false,
  };
}

function onLoadShiftHistoryPositions(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isShiftHistoryPositionsLoading: true,
  };
}

function onLoadShiftHistoryPositionsSuccess(
  state: IViewAgencyWorkerState,
  { payload }: { payload: ILoadShiftHistoryPositionsSuccessPayload },
): IViewAgencyWorkerState {
  const { agency_id, positions } = payload;

  return {
    ...state,
    shiftHistoryPositionsByAgency: {
      ...state.shiftHistoryPositionsByAgency,
      [agency_id]: {
        positions,
      },
    },
    isShiftHistoryPositionsLoading: false,
  };
}

function onLoadShiftHistoryPositionsError(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isShiftHistoryPositionsLoading: false,
  };
}

function onLoadChecklists(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isChecklistsLoading: true,
  };
}

function onLoadChecklistsSuccess(
  state: IViewAgencyWorkerState,
  { payload }: { payload: ILoadChecklistsSuccessPayload },
): IViewAgencyWorkerState {
  const { agency_id, records, totalCount, filteredCount, filters } = payload;

  return {
    ...state,
    checklistsByAgency: {
      ...state.checklistsByAgency,
      [agency_id]: {
        records,
        totalCount,
        filteredCount,
        filters,
      },
    },
    isChecklistsLoading: false,
  };
}

function onLoadChecklistsError(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isChecklistsLoading: false,
  };
}

function onLoadChecklistDetail(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isChecklistDetailLoading: true,
  };
}

function onLoadChecklistDetailSuccess(
  state: IViewAgencyWorkerState,
  { payload }: { payload: ILoadChecklistDetailSuccessPayload },
): IViewAgencyWorkerState {
  return {
    ...state,
    checklistDetail: payload.checklist,
    isChecklistDetailLoading: false,
  };
}

function onLoadChecklistDetailError(state: IViewAgencyWorkerState): IViewAgencyWorkerState {
  return {
    ...state,
    isChecklistDetailLoading: false,
  };
}

export default createReducer(initState, {
  [actionTypes.LOAD_DATA]: onLoad,
  [actionTypes.LOAD_DATA_SUCCESS]: onLoadSuccess,
  [actionTypes.LOAD_DATA_ERROR]: onLoadError,
  [actionTypes.CLEAR_STORE]: onClearStore,
  [actionTypes.LOAD_SHIFTS]: onLoadShifts,
  [actionTypes.LOAD_SHIFTS_SUCCESS]: onLoadShiftsSuccess,
  [actionTypes.LOAD_SHIFTS_ERROR]: onLoadShiftsError,
  [actionTypes.LOAD_TENURE_TRACKS]: onLoadTenureTracks,
  [actionTypes.LOAD_TENURE_TRACKS_SUCCESS]: onLoadTenureTracksSuccess,
  [actionTypes.LOAD_TENURE_TRACKS_ERROR]: onLoadTenureTracksError,
  [actionTypes.LOAD_SHIFT_HISTORY_POSITIONS]: onLoadShiftHistoryPositions,
  [actionTypes.LOAD_SHIFT_HISTORY_POSITIONS_SUCCESS]: onLoadShiftHistoryPositionsSuccess,
  [actionTypes.LOAD_SHIFT_HISTORY_POSITIONS_ERROR]: onLoadShiftHistoryPositionsError,
  [actionTypes.LOAD_CHECKLISTS]: onLoadChecklists,
  [actionTypes.LOAD_CHECKLISTS_SUCCESS]: onLoadChecklistsSuccess,
  [actionTypes.LOAD_CHECKLISTS_ERROR]: onLoadChecklistsError,
  [actionTypes.LOAD_CHECKLIST_DETAIL]: onLoadChecklistDetail,
  [actionTypes.LOAD_CHECKLIST_DETAIL_SUCCESS]: onLoadChecklistDetailSuccess,
  [actionTypes.LOAD_CHECKLIST_DETAIL_ERROR]: onLoadChecklistDetailError,
});
